import casinoApi from 'api/casinoApi';
import chatApi from 'api/chatApi';
import userApi from 'api/userApi';
import walletApi from 'api/walletApi';
import { useAppDispatch, useAppTranslation } from 'app/hooks';
import { useChatConntext } from 'components/Hoc/MessageProvider';
import { withLiveOddsUpdate } from 'components/Hoc/withLiveOddsUpdate';
import { showSuccessModal } from 'components/Modals/CommonModals';
import { KEY_THEME_LOCALSTORAGE, LAST_AUTO_CHANGE_THEME_LOCALSTORAGE } from 'consts/app';
import CasinoPage from 'features/casino/pages';
import AllGamePage from 'features/casino/pages/AllGamePage';
import GamePlay from 'features/casino/pages/GamePlay';
import EventNoti from 'features/event/EventNoti';
import SpinWheelPopup from 'features/home/components/Modals/SpinWheelModal';
import ParachuteItems from 'features/home/components/ParachuteItems';
import { setCurrentConverter, setCurrentTurbo, setShowEventNoti } from 'features/home/homeSlice';
import AllStreamPage from 'features/home/pages/AllStreamer';
import StreamerPage from 'features/home/pages/Streamer';
import Streaming from 'features/home/pages/Streaming';
import KYC from 'features/kyc/pages';
import ListBetPage from 'features/listBet/pages';
import VerifyEmail from 'features/verifyEmail/pages';
import useMutiLanguage from 'hooks/useMultiLanguage';
import usePlatform from 'hooks/usePlatform';
import UserInactivityDetector from 'hooks/UserInactivityDetector';
import useThemes from 'hooks/useThemes';
import useUserDetail from 'hooks/useUserDetail';
import { GiveAwayResponseModel } from 'models/chatMessage';
import 'moment/locale/es';
import 'moment/locale/fr';
import 'moment/locale/km';
import 'moment/locale/ru';
import 'moment/locale/th';
import 'moment/locale/tr';
import 'moment/locale/zh-cn';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
import { setPlatforms } from 'redux/casinoSlice';
import { setIsDarkMode, setTbetPrice } from 'redux/userSlice';
import { isShowEventNoti } from 'utils/commonUtils';
import { getLocalStorage, setLocalStorage } from 'utils/sessionStorage';
import NotFoundPage from './components/Commons/NotFoundPage';
import HomePage from './features/home/pages';

const App: React.FunctionComponent = () => {
  const dispatch = useAppDispatch();
  usePlatform();
  const t = useAppTranslation();
  const { getUserDetail, isUserLoggedIn } = useUserDetail();
  const { currentTheme } = useThemes();
  const themeRef = useRef<string>(currentTheme);
  const [giveawaysSpin, setGiveawaysSpin] = useState<GiveAwayResponseModel[]>([]);
  const [currentPopupIndex, setCurrentPopupIndex] = useState<number>(-1);
  const { dropGiveAways, removeGiveAways, spinGiveAways, removeSpinAway } = useChatConntext();

  const getTbetPrice = async () => {
    const { ok, body } = await walletApi.getTokenPrice('$TBET');
    if (ok && body) {
      dispatch(setTbetPrice(body.price));
    }
  };

  useMutiLanguage();

  useEffect(() => {
    themeRef.current = currentTheme;
  }, [currentTheme]);

  useEffect(() => {
    if (isUserLoggedIn) {
      getUserDetail();
    }
  }, [isUserLoggedIn]);

  useEffect(() => {
    getTbetPrice();
    const converter = getLocalStorage('converter');
    if (!converter) {
      dispatch(setCurrentConverter('american'));
      setLocalStorage('converter', 'american');
    } else dispatch(setCurrentConverter(converter));

    dispatch(setCurrentTurbo(''));

    const temp = getLocalStorage(KEY_THEME_LOCALSTORAGE);
    if (temp) {
      dispatch(setIsDarkMode(temp === 'dark'));
    } else {
      dispatch(setIsDarkMode(true));
      setLocalStorage(KEY_THEME_LOCALSTORAGE, 'dark');
    }
  }, []);

  useEffect(() => {
    let timerInterval: NodeJS.Timeout;

    timerInterval = setInterval(() => {
      const now = new Date();
      const ts = getLocalStorage(LAST_AUTO_CHANGE_THEME_LOCALSTORAGE);
      if (!ts) {
        const currentHour = now.getHours();
        const isLightMode = currentHour >= 6 && currentHour < 18;
        if (
          (isLightMode && themeRef.current === 'dark') ||
          (!isLightMode && themeRef.current === 'light')
        ) {
          document.getElementById('btn-change-theme')?.click();
          setLocalStorage(LAST_AUTO_CHANGE_THEME_LOCALSTORAGE, now.getTime());
        } else {
          setLocalStorage(LAST_AUTO_CHANGE_THEME_LOCALSTORAGE, now.getTime());
        }
      } else {
        const lastUpdate = new Date(Number(ts));
        const currentHour = now.getHours();
        const lastUpdateHour = lastUpdate.getHours();
        const isLightMode = currentHour >= 6 && currentHour < 18;
        if (now.getDate() !== lastUpdate.getDate()) {
          document.getElementById('btn-change-theme')?.click();
          setLocalStorage(LAST_AUTO_CHANGE_THEME_LOCALSTORAGE, now.getTime());
        } else if (
          (isLightMode && (lastUpdateHour < 6 || lastUpdateHour >= 18)) ||
          (!isLightMode && lastUpdateHour >= 6 && lastUpdateHour < 18)
        ) {
          document.getElementById('btn-change-theme')?.click();
          setLocalStorage(LAST_AUTO_CHANGE_THEME_LOCALSTORAGE, now.getTime());
        }
      }
    }, 1000);

    return () => {
      clearInterval(timerInterval);
    };
  }, []);

  const handleClosePopup = () => {
    const nextIndex = currentPopupIndex + 1;
    setGiveawaysSpin((prev) => prev.slice(1));
    if (nextIndex < giveawaysSpin.length) {
      setCurrentPopupIndex(nextIndex);
    } else {
      setCurrentPopupIndex(-1);
    }
  };

  const hideAllSpins = () => {
    setGiveawaysSpin([]);
    setCurrentPopupIndex(-1);
  };

  const handleClaimPresent = async (promotionCode: string) => {
    const { ok, body, error } = await userApi.usePromotion({
      promotionCode: promotionCode,
    });
    if (ok && body) {
      showSuccessModal({
        title: t('give-away.drop.claimed'),
        content: t('give-away.drop.content', {
          amount: body.amount,
          walletReceive: body.walletReceive,
        }),
        onOk: () => {},
      });
    }
  };

  useEffect(() => {
    if (isShowEventNoti()) {
      dispatch(setShowEventNoti(true));
    }
  }, [isShowEventNoti]);

  const fetchData = async () => {
    const { ok, body } = await casinoApi.getPlatform();
    if (ok && body) {
      dispatch(setPlatforms(body));
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (spinGiveAways.length > 0) {
      setGiveawaysSpin((prev) => [...spinGiveAways, ...prev]);
      removeSpinAway();
    }
  }, [spinGiveAways.length]);

  useEffect(() => {
    setCurrentPopupIndex(0);
  }, [spinGiveAways.length]);

  const getGiveaways = async () => {
    const { ok, body } = await chatApi.getSpinAndDrop();
    if (ok && body) {
      const spinGiveaways = body.filter((item) => item.promotionType === 'SPIN');
      setGiveawaysSpin(spinGiveaways);

      if (spinGiveaways.length > 0) {
        setCurrentPopupIndex(0);
      }
    }
  };

  useEffect(() => {
    if (isUserLoggedIn) {
      getGiveaways();
    }
  }, [isUserLoggedIn]);

  return (
    <Fragment>
      <UserInactivityDetector />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/kyc" element={<KYC />} />
        <Route path="/streaming" element={<StreamerPage />} />
        <Route path="/stream/all-streamer" element={<AllStreamPage />} />
        <Route path="/streaming/streamer" element={<Streaming />} />
        <Route path="/casino" element={<CasinoPage />} />
        <Route path="/casino/game/:gameId/play" element={<GamePlay />} />
        <Route path="/casino/all-game" element={<AllGamePage />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/bet" element={<ListBetPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <EventNoti />
      {currentPopupIndex >= 0 && currentPopupIndex < giveawaysSpin.length && (
        <SpinWheelPopup
          isOpen={true}
          onClose={handleClosePopup}
          giveAwayData={giveawaysSpin[currentPopupIndex]}
          onKYCRequired={hideAllSpins}
        />
      )}
      <ParachuteItems
        giveAwayData={dropGiveAways}
        onItemClick={handleClaimPresent}
        onKYCRequired={hideAllSpins}
      />
    </Fragment>
  );
};

export default withLiveOddsUpdate(App);
